import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Form, Icon } from 'semantic-ui-react';
import Page from './Page';

export default function ResetPassword() {
  const history = useHistory();
  const { token } = useParams();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Eror. Por favor solicita uno nuevo.');
        setIsValidating(false);
        setTimeout(() => history.push('/login'), 3000);
        return;
      }

      try {
        const response = await fetch(`/verify-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (data.valid) {
          setTokenValid(true);
          setIsValidating(false);
        } else {
          setError('Link inválido o expirado. Por favor solicita uno nuevo.');
          setIsValidating(false);
          setTimeout(() => history.push('/login'), 3000);
        }
      } catch (err) {
        console.error('Error:', err);
        setError('Error de conexión. Por favor intenta de nuevo.');
        setIsValidating(false);
        setTimeout(() => history.push('/login'), 3000);
      }
    };

    validateToken();
  }, [token, history]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    const passwordRegex = /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;

    if (!passwordRegex.test(newPassword)) {
      setError('La contraseña debe tener al menos 8 caracteres, una letra mayúscula y un símbolo');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch(`/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });

      const data = await response.json();

      if (data.success) {
        setMessage('Contraseña cambiada exitosamente');
        setTimeout(() => history.push('/login'), 3000);
      } else {
        setError(data.message);
        // Si el error indica que el token es inválido, redirigir
        if (data.message.includes('expirado') || data.message.includes('utilizado') || data.message.includes('no encontrado')) {
          setTimeout(() => history.push('/login'), 4000);
        }
      }
    } catch (err) {
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#f3f4f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    },
    card: {
      background: 'white',
      borderRadius: '24px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      padding: 'clamp(30px, 5vw, 50px)',
      maxWidth: '500px',
      width: '100%',
      animation: 'fadeInUp 0.5s ease-out'
    },
    header: {
      marginBottom: 'clamp(25px, 5vh, 40px)',
      textAlign: 'center'
    },
    title: {
      fontSize: 'clamp(1.8em, 5vw, 2.5em)',
      fontWeight: 700,
      color: '#1f2937',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      flexWrap: 'wrap'
    },
    subtitle: {
      color: '#6b7280',
      fontSize: 'clamp(0.85em, 3vw, 1em)',
      fontWeight: 400,
      marginTop: '8px'
    },
    inputWrapper: {
      marginBottom: '24px'
    },
    messageSuccess: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
      padding: '14px 18px',
      borderRadius: '12px',
      marginBottom: '24px',
      textAlign: 'center',
      fontSize: '0.9em',
      borderLeft: '4px solid #10b981'
    },
    messageError: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      padding: '14px 18px',
      borderRadius: '12px',
      marginBottom: '24px',
      textAlign: 'center',
      fontSize: '0.9em',
      borderLeft: '4px solid #ef4444'
    },
    passwordHint: {
      fontSize: '12px',
      color: '#9ca3af',
      textAlign: 'center',
      marginTop: '8px',
      marginBottom: '16px'
    },
    backLink: {
      textAlign: 'center',
      marginTop: '24px'
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '40px'
    }
  };

  if (isValidating) {
    return (
      <Page>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.loadingContainer}>
              <Icon name="circle notched" loading size="huge" style={{ color: '#667eea' }} />
              <p style={{ marginTop: '20px', color: '#6b7280' }}>Validando link de recuperación...</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }


  if (!tokenValid) {
    return (
      <Page>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.header}>
              <div style={styles.title}>
                <Icon name="warning circle" size="large" style={{ color: '#ef4444' }} />
                <span>Link Inválido</span>
              </div>
            </div>

            <div style={styles.messageError}>
              <Icon name="exclamation circle" />
              {error || 'Este link ya no es válido. Por favor solicita uno nuevo'}
            </div>

            <Button
              className="reset-button"
              fluid
              size='large'
              onClick={() => history.push('/login')}
            >
              <Icon name="arrow left" />
              Volver al login
            </Button>
          </div>
        </div>
      </Page>
    );
  }

  // Formulario de cambio de contraseña (válido)
  return (
    <Page>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.title}>
              <Icon name="key" size="large" />
              <span style={{ whiteSpace: 'nowrap' }}>Cambiar Contraseña</span>
            </div>
            <div style={styles.subtitle}>
              Ingresa tu nueva contraseña
            </div>
          </div>

          {message && (
            <div style={styles.messageSuccess}>
              <Icon name="check circle" />
              {message}
            </div>
          )}

          {error && (
            <div style={styles.messageError}>
              <Icon name="exclamation circle" />
              {error}
            </div>
          )}

          <Form size='large' loading={loading} onSubmit={handleSubmit}>
            <div style={styles.inputWrapper}>
              <div style={{ position: 'relative' }}>
                <Form.Input
                  className="reset-form-input password-input"
                  fluid
                  icon='lock'
                  iconPosition='left'
                  placeholder='Nueva contraseña'
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#667eea',
                    zIndex: 10,
                    borderRadius: '8px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <Icon name={showPassword ? 'eye slash' : 'eye'} />
                </button>
              </div>
            </div>

            <div style={styles.inputWrapper}>
              <div style={{ position: 'relative' }}>
                <Form.Input
                  className="reset-form-input password-input"
                  fluid
                  icon='lock'
                  iconPosition='left'
                  placeholder='Confirmar contraseña'
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#667eea',
                    zIndex: 10,
                    borderRadius: '8px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <Icon name={showConfirmPassword ? 'eye slash' : 'eye'} />
                </button>
              </div>
            </div>

            <div style={styles.passwordHint}>
              <Icon name="info circle" size="small" />
              Mínimo 8 caracteres, una mayúscula y un símbolo
            </div>

            <Button
              className="reset-button"
              fluid
              size='large'
              type="submit"
              loading={loading}
            >
              <Icon name="save" />
              Cambiar Contraseña
            </Button>

            <div style={styles.backLink}>
              <button
                type="button"
                onClick={() => history.push('/login')}
                className="reset-link"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.9em'
                }}
              >
                ← Volver al login
              </button>
            </div>
          </Form>
        </div>
      </div>
    </Page>
  );
}